// src/components/modals/PayGRNModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, DollarSign, CreditCard, FileText, Loader2, ChevronDown } from 'lucide-react'
import GRNService from '../../services/grnService'
import { PAYMENT_METHODS, PAYMENT_STATUS } from '../../utils/grnModel'

const PayGRNModal = ({ grn, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    grnId: grn._id,
    grnNumber: grn.grnNumber,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: '', // API uses 'paymentType' not 'paymentMethod'
    amountPaid: '', // Start with empty string instead of 0
    note: '' // API uses 'note' not 'notes'
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // FIXED: Proper balance due calculation
  const calculateBalanceDue = () => {
    // If remainingPayment exists and is a number, use it (even if 0)
    if (typeof grn.remainingPayment === 'number') {
      return Math.max(0, grn.remainingPayment) // Ensure non-negative
    }
    
    // If remainingPayment is null/undefined but we have paidAmount, calculate
    if (grn.totalAmount && typeof grn.paidAmount === 'number') {
      const remaining = grn.totalAmount - grn.paidAmount
      return Math.max(0, remaining)
    }
    
    // Fallback to total amount
    return grn.totalAmount || 0
  }

  const balanceDue = calculateBalanceDue()

  // FIXED: Check if GRN is fully paid
  const isFullyPaid = () => {
    if (grn.paymentStatus === PAYMENT_STATUS.FULLY_PAID) {
      return true
    }
    
    // Also check by balance calculation
    return balanceDue <= 0
  }

  const fullyPaid = isFullyPaid()

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amountPaid' ? value : value // Keep as string for amount field
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required'
    }

    if (!formData.paymentType) {
      newErrors.paymentType = 'Payment method is required'
    }

    // FIXED: Better validation for fully paid GRNs
    if (fullyPaid) {
      newErrors.general = 'This GRN has been fully paid'
      return false
    }

    // Convert to number for validation
    const amountPaid = parseFloat(formData.amountPaid) || 0
    
    if (!formData.amountPaid || formData.amountPaid.trim() === '' || amountPaid <= 0) {
      newErrors.amountPaid = 'Amount paid must be greater than 0'
    } else if (amountPaid > balanceDue) {
      newErrors.amountPaid = 'Amount paid cannot exceed balance due'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      console.log('💰 Recording payment for GRN:', formData)
      
      // Format payment data for API
      const paymentData = GRNService.formatPaymentForAPI(formData)
      const response = await GRNService.createGRNPayment(paymentData)
      
      if (response?.success) {
        onSuccess()
      }
    } catch (error) {
      console.error('❌ Error recording payment:', error)
    } finally {
      setLoading(false)
    }
  }

  // Handle background click
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Get payment method configuration
  const getPaymentMethodConfig = (method) => {
    const configs = {
      [PAYMENT_METHODS.CASH]: { 
        icon: DollarSign, 
        label: 'Cash', 
        color: 'text-green-600',
        description: 'Cash payment'
      },
      [PAYMENT_METHODS.CHEQUE]: { 
        icon: FileText, 
        label: 'Cheque', 
        color: 'text-blue-600',
        description: 'Bank cheque payment'
      },
      [PAYMENT_METHODS.BANK_TRANSFER]: { 
        icon: CreditCard, 
        label: 'Bank Transfer', 
        color: 'text-purple-600',
        description: 'Electronic bank transfer'
      },
      [PAYMENT_METHODS.CARD]: { 
        icon: CreditCard, 
        label: 'Card', 
        color: 'text-orange-600',
        description: 'Credit/Debit card payment'
      }
    }
    return configs[method] || { icon: DollarSign, label: method, color: 'text-gray-600' }
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-8 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-white flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Pay for GRN</h2>
          </div>
           <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* FIXED: Better status display for fully paid GRNs */}
            {fullyPaid ? (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-lg font-medium text-green-900">✅ Fully Paid</span>
                    <p className="text-sm text-green-700 mt-1">This GRN has been completely paid</p>
                  </div>
                  <span className="text-2xl font-bold text-green-900">{formatCurrency(grn.totalAmount || 0)}</span>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-blue-900">Due Balance:</span>
                  <span className="text-2xl font-bold text-blue-900">{formatCurrency(balanceDue)}</span>
                </div>
              </div>
            )}

            {/* Show error if trying to pay fully paid GRN */}
            {errors.general && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* GRN Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GRN Number*
                </label>
                <Input
                  name="grnNumber"
                  value={formData.grnNumber}
                  readOnly
                  className="bg-gray-50"
                />
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date
                </label>
                <Input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  disabled={fullyPaid}
                  className={errors.paymentDate ? 'border-red-500' : ''}
                />
                {errors.paymentDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.paymentDate}</p>
                )}
              </div>

              {/* FIXED: Payment Method with Chevron Icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <div className="relative">
                  <select
                    name="paymentType"
                    value={formData.paymentType}
                    onChange={handleInputChange}
                    disabled={fullyPaid}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                      errors.paymentType ? 'border-red-500' : 'border-gray-300'
                    } ${fullyPaid ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
                  >
                    <option value="">Select method</option>
                    {Object.values(PAYMENT_METHODS).map(method => {
                      const config = getPaymentMethodConfig(method)
                      return (
                        <option key={method} value={method}>
                          {config.label}
                        </option>
                      )
                    })}
                  </select>
                  {/* FIXED: Added Chevron Icon */}
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
                {errors.paymentType && (
                  <p className="mt-1 text-sm text-red-600">{errors.paymentType}</p>
                )}
              </div>

              {/* FIXED: Amount Paid - blocked when fully paid */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount Paid *
                </label>
                <Input
                  type="number"
                  name="amountPaid"
                  value={formData.amountPaid}
                  onChange={handleInputChange}
                  min="0"
                  max={fullyPaid ? 0 : balanceDue}
                  step="0.01"
                  placeholder={fullyPaid ? "GRN fully paid" : "Enter amount to pay"}
                  disabled={fullyPaid}
                  className={`${errors.amountPaid ? 'border-red-500' : ''} ${fullyPaid ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  onFocus={(e) => {
                    // Select all text when field is focused for easy replacement
                    if (!fullyPaid) e.target.select()
                  }}
                />
                {errors.amountPaid && (
                  <p className="mt-1 text-sm text-red-600">{errors.amountPaid}</p>
                )}
                {!fullyPaid && (
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum: {formatCurrency(balanceDue)}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes {!fullyPaid && '*'}
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows={3}
                  disabled={fullyPaid}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fullyPaid ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={fullyPaid ? "GRN fully paid - no payment needed" : "Additional notes about receipt no, cheque no.."}
                />
              </div>

              {/* Payment Summary - only show if not fully paid */}
              {!fullyPaid && formData.amountPaid && parseFloat(formData.amountPaid) > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-gray-900">Payment Summary</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount Paying:</span>
                    <span className="font-medium">{formatCurrency(parseFloat(formData.amountPaid) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remaining Balance:</span>
                    <span className="font-medium">{formatCurrency(balanceDue - (parseFloat(formData.amountPaid) || 0))}</span>
                  </div>
                  {formData.paymentType && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="font-medium">{getPaymentMethodConfig(formData.paymentType).label}</span>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {fullyPaid ? 'Close' : 'Cancel'}
          </Button>
          {/* FIXED: Hide save button when fully paid */}
          {!fullyPaid && (
            <Button
              onClick={handleSubmit}
              disabled={loading || !formData.amountPaid || !formData.paymentType || parseFloat(formData.amountPaid) <= 0}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DollarSign className="h-4 w-4" />
              )}
              <span>Save Payment</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PayGRNModal